import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, XCircle, ExternalLink, Film } from "lucide-react";
import { videoDiagnostics } from "@/lib/api";

// Panneau de diagnostic vidéo : contrôle chaque vidéo du site (taille, démarrage rapide)
// et signale celles à optimiser pour un chargement rapide.

const STATUS = {
  optimisee: { label: "Optimisée", icon: CheckCircle2, cls: "text-green-400 border-green-400/40" },
  a_verifier: { label: "À vérifier", icon: AlertTriangle, cls: "text-amber-400 border-amber-400/40" },
  a_optimiser: { label: "Optimisation recommandée", icon: XCircle, cls: "text-red-400 border-red-400/40" },
  externe: { label: "Externe", icon: ExternalLink, cls: "text-sky-400 border-sky-400/40" },
};

export const AdminVideoDiagnostics = () => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkedAt, setCheckedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await videoDiagnostics();
      setRows(res.items || []);
      setCheckedAt(new Date());
    } catch { toast.error("Diagnostic impossible."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = (rows || []).reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-5" data-testid="admin-video-diagnostics">
      <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 text-sm text-secondary2 flex items-start gap-3">
        <Film size={18} className="text-electric shrink-0 mt-0.5" />
        <span>Contrôle automatique de <strong className="text-white">toutes les vidéos du site</strong> (accueil, réalisations, actualités, galeries, annonces…) : poids du fichier et <strong className="text-white">démarrage rapide</strong> (index « faststart »). Une vidéo lourde ou mal encodée met longtemps à démarrer chez vos visiteurs.</span>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-xs">
          {rows !== null && (
            <>
              <span className="text-green-400">{counts.optimisee || 0} optimisée(s)</span>
              <span className="text-white/30">·</span>
              <span className="text-amber-400">{counts.a_verifier || 0} à vérifier</span>
              <span className="text-white/30">·</span>
              <span className="text-red-400">{counts.a_optimiser || 0} à optimiser</span>
              <span className="text-white/30">·</span>
              <span className="text-sky-400">{counts.externe || 0} externe(s)</span>
            </>
          )}
        </div>
        <button onClick={load} disabled={loading} data-testid="videodiag-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white text-xs tracking-[0.1em] uppercase hover:border-electric hover:text-electric transition-colors disabled:opacity-60">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Relancer le diagnostic
        </button>
      </div>

      {rows === null ? (
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-electric" /></div>
      ) : rows.length === 0 ? (
        <p className="text-secondary2 text-sm py-8 text-center">Aucune vidéo détectée sur le site.</p>
      ) : (
        <div className="space-y-2" data-testid="videodiag-list">
          {rows.map((r, i) => {
            const st = STATUS[r.status] || STATUS.a_verifier;
            const Icon = st.icon;
            return (
              <div key={i} data-testid={`videodiag-row-${i}`} className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 flex flex-wrap items-center gap-4">
                <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] border rounded-full px-2.5 py-1 shrink-0 ${st.cls}`} data-testid={`videodiag-status-${i}`}>
                  <Icon size={12} /> {st.label}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{r.source || "Vidéo"}</div>
                  <div className="text-[11px] text-secondary2 truncate">{r.url}</div>
                  <div className="text-[11px] text-white/60 mt-1">{r.detail}</div>
                </div>
                {r.kind === "fichier" && (
                  <div className="text-right text-[11px] text-secondary2 shrink-0">
                    {typeof r.sizeMb === "number" && <div>{r.sizeMb} Mo</div>}
                    {r.faststart === true && <div className="text-green-400/80">Démarrage rapide</div>}
                    {r.faststart === false && <div className="text-red-400/80">Démarrage lent</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {checkedAt && <p className="text-[11px] text-secondary2">Dernier contrôle : {checkedAt.toLocaleString("fr-FR")}. Pour optimiser une vidéo : ré-encodez-la (ex. HandBrake, case « Web optimized » cochée, 1080p ~8 Mbps) puis remplacez-la.</p>}
    </div>
  );
};
